
import {
  Vue, Component, Prop,
} from 'vue-property-decorator';
import moment from 'moment';
import API from '@/lib/API';
import TaskDetailsTitleCircle from '@/components/TaskDetailsTitleCircle.vue';
import UpdateButton from '@/components/UpdateButton.vue';
import {
  Lead, LeadType, PermissionCode, StepNames, Task, User,
} from '@/types';
import { canResetTask, currentUserHasPermission } from '@/helpers';

moment.locale('it');

@Component({
  components: {
    TaskDetailsTitleCircle,
    UpdateButton,
  },
})
export default class TaskDetailsTitle extends Vue {
  @Prop(Object) readonly task: Task | undefined;
  @Prop(String) readonly step: StepNames | undefined;
  @Prop({ type: Boolean, default: false }) readonly ecobonusCheck!: boolean;

  // Mostra le eventuali azioni possibili sul Task (Modifica, Reimposta)
  @Prop({ type: Boolean, default: false }) readonly showActions!: boolean;

  get selectedLead(): Lead | null {
    return this.$store.getters.currentLead;
  }

  get selectedTask(): Task | null {
    // Se non è stato specificato un Task nelle Prop,
    // utilizza il Task selezionato memorizzato tramite Vuex.
    return this.task ?? this.$store.getters.selectedTask;
  }

  get leadType(): LeadType | null {
    return this.selectedLead?.type ?? null;
  }

  // Sto caricando il Task
  get loading(): boolean {
    return this.$store.getters.isTaskLoading;
  }

  // Sto aggiornando il Task
  get updating(): boolean {
    return this.$store.getters.isTaskUpdating;
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get isSuperUser(): boolean {
    return this.user && this.user.isSuperUser;
  }

  get isOpen(): boolean {
    if (this.selectedTask) return this.selectedTask.status === null;
    if (this.step && this.selectedLead) return this.selectedLead[this.step] === null;
    return false;
  }

  get isComplete(): boolean {
    if (this.selectedTask) return this.selectedTask.status === 'completed';
    if (this.step && this.selectedLead) return this.selectedLead[this.step] === true;
    return false;
  }

  get isFailed(): boolean {
    if (this.selectedTask) return this.selectedTask.status === 'error';
    if (this.step && this.selectedLead) return this.selectedLead[this.step] === false;
    return false;
  }

  get showUpdateButton(): boolean {
    if (this.task || !this.selectedTask) return false;

    if (this.selectedTask.status === null) return true;

    if (this.selectedTask.name === 'Cm' && this.hasPermission('completedTaskCm.edit')) return true;

    return false;
  }

  get canResetTask(): boolean {
    return canResetTask(this.selectedTask, this.selectedLead);
  }

  get canEditTask(): boolean {
    if (!this.selectedTask) return false;

    // Se il task è in lavorazione, consente la modifica del task
    if (this.isOpen) return true;

    // Se il task è fallito, non consente la modifica del task
    if (this.isFailed) return false;

    // Il Task è completato

    switch (this.selectedTask.name) {
      case 'Cm':
        return this.hasPermission('completedTaskCm.edit');

      default:
        break;
    }

    return false;
  }

  get eturnityIntegration() {
    return this.$store.getters.getIntegrations.eturnity;
  }

  get showEturnityButton(): boolean {
    // L'integrazione e' spenta e non verra' riaccesa: il controllo va fatto
    // qui e non solo sul permesso, che i Super Utenti scavalcano.
    if (!this.eturnityIntegration.enabled) return false;
    // Mostra il pulsante solo per i Task di tipo 'P' (Preventivo)
    return this.selectedTask?.name === 'P' && this.hasPermission('eturnity.create_config_from_lead');
  }

  get stepTitle(): string {
    if (!this.step || !this.selectedLead) return '';

    const stepValue = this.selectedLead[this.step];

    switch (this.step) {
      case 'step1':
        if (stepValue === false) return 'Il cliente non è stato contattato telefonicamente';
        if (stepValue === true) return 'Il cliente è stato contattato telefonicamente';
        return 'Il cliente è stato contattato telefonicamente?';

      case 'step2':
        if (stepValue === false) return 'L\'appuntamento per il sopralluogo non è stato fissato';
        if (stepValue === true) return 'L\'appuntamento per il sopralluogo è stato fissato';
        return 'L\'appuntamento per il sopralluogo è stato fissato?';

      case 'step3':
        if (stepValue === false) return 'Il preventivo non è stato consegnato al cliente';
        if (stepValue === true) return 'Il preventivo è stato consegnato al cliente';
        return 'Il preventivo è stato consegnato al cliente?';

      case 'step4':
        if (stepValue === false) return 'Il preventivo è stato rifiutato';
        if (stepValue === true) return 'Il preventivo è stato accettato';
        return 'Il preventivo è stato accettato?';

      default:
        return 'Task senza titolo';
    }
  }

  get title(): string {
    if (this.ecobonusCheck) return 'Test di fattibilità 110%';

    if (this.selectedTask) return this.selectedTask.description || '';

    if (this.step) return this.stepTitle;

    return 'Task senza titolo';
  }

  get subtitle(): string | null {
    switch (this.selectedLead?.type) {
      case 'installatore-figlia':
        return 'Lead figlia generica';

      case 'elettrica-figlia':
        return 'Lead figlia elettrica';

      case 'termica-figlia':
        return 'Lead figlia termica';

      default:
        break;
    }

    return null;
  }

  get dateTime() {
    if (this.ecobonusCheck && this.selectedLead?.ecCompletedAt) {
      return this.mDateTime(this.selectedLead.ecCompletedAt);
    }

    if (this.selectedTask && this.selectedTask.status === 'completed'
      && this.selectedTask.completedAt) return this.mDateTime(this.selectedTask.completedAt);

    if (this.selectedTask && this.selectedTask.status === 'error'
      && this.selectedTask.errorAt) return this.mDateTime(this.selectedTask.errorAt);

    return null;
  }

  hasPermission(codename: PermissionCode) { return currentUserHasPermission(codename); }

  mDateTime(date: Date) {
    return `${moment(date).format('L')} ore ${moment(date).format('LT')}`;
  }

  onReset() {
    console.log(`${this.constructor.name}: onReset()`); // eslint-disable-line no-console

    // this.resetDialog = true;
    this.$emit('reset');
  }

  onEdit() {
    console.log(`${this.constructor.name}: onEdit()`); // eslint-disable-line no-console

    this.$emit('edit');
  }

  onSave() {
    console.log(`${this.constructor.name}: onSave()`); // eslint-disable-line no-console

    this.$emit('save');
  }

  async openEturnityCalculator() {
    console.log(`${this.constructor.name}: openEturnityCalculator()`); // eslint-disable-line no-console

    if (!this.selectedLead) {
      console.error('Nessuna lead selezionata');
      return;
    }

    try {
      // Chiama l'endpoint per creare una nuova configurazione Eturnity
      const response = await API.execute('post', `/lead/${this.selectedLead.id}/eturnity-config`);

      if (response.success && response.configId) {
        // Apre solo il calcolatore solare di Eturnity in una nuova scheda.
        // L'indirizzo arriva dal backend: quello cablato puntava allo spazio
        // del cliente precedente.
        const eturnityUrl = `${this.eturnityIntegration.calculatorUrl}?utm_source=${response.configId}`;
        window.open(eturnityUrl, '_blank');

        // Forza l'aggiornamento della lead corrente per far apparire l'EturnityWaitingDialog
        console.log('Calcolatore solare Eturnity aperto per configurazione multipla:', response.configId);
        console.log('Aggiornamento della lead corrente per mostrare EturnityWaitingDialog...');

        // Aggiorna la lead corrente per far apparire il dialog
        await this.$store.dispatch('updateProject', { id: this.selectedLead.id });
      } else {
        console.error('Errore nella risposta del server:', response);
      }
    } catch (error) {
      console.error('Errore durante la creazione della configurazione Eturnity:', error);

      // Mostra un messaggio di errore all'utente se disponibile
      if ((error as any)?.response?.data?.error) {
        this.$store.dispatch('showAlert', {
          type: 'error',
          text: (error as any).response.data.error,
        });
      } else {
        this.$store.dispatch('showAlert', {
          type: 'error',
          text: 'Errore durante l\'avvio del calcolatore solare. Riprova più tardi.',
        });
      }
    }
  }
}
